<template>
  <div id="data-quality">
    <div class="container-fluid pt-4 px-4">
      <v-alert type="info" variant="tonal" density="comfortable" class="mb-4" rounded="lg">
        "Your strategy is only as good as its data." Scoped to the active watchlist — checks whether each stock has
        enough daily history to classify, enough historical gap events to judge, complete recent intraday candles,
        and no suspected stock split/bonus (which would corrupt win-rate/return numbers if untreated).
        <v-btn variant="text" size="small" :loading="!loaded" @click="load" class="ml-2">
          <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-1" /> Refresh
        </v-btn>
      </v-alert>

      <v-row class="mb-1" dense>
        <v-col cols="12" sm="6" md="2">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Watchlist stocks</div>
              <div class="stat-value">{{ summary.stockCount ?? '—' }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Missing history</div>
              <div class="stat-value" :class="summary.missingHistory > 0 ? 'text-error' : 'text-success'">{{ summary.missingHistory ?? '—' }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Missing gap data</div>
              <div class="stat-value" :class="summary.missingGapData > 0 ? 'text-error' : 'text-success'">{{ summary.missingGapData ?? '—' }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Missing candles</div>
              <div class="stat-value" :class="summary.missingCandles > 0 ? 'text-error' : 'text-success'">{{ summary.missingCandles ?? '—' }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Corp. action suspected</div>
              <div class="stat-value" :class="summary.corporateActionSuspected > 0 ? 'text-error' : 'text-success'">{{ summary.corporateActionSuspected ?? '—' }}</div>
            </v-card-text>
          </v-card>
        </v-col>
        <v-col cols="12" sm="6" md="2">
          <v-card flat rounded="lg" class="gap-backtest-card stat-tile">
            <v-card-text class="px-5 py-4">
              <div class="text-caption text-medium-emphasis">Candle coverage</div>
              <div class="stat-value">{{ isFiniteNumber(summary.avgCandleCoveragePct) ? `${summary.avgCandleCoveragePct}%` : '—' }}</div>
              <div class="text-caption text-medium-emphasis mt-1" v-if="summary.tradingDaysChecked">
                last {{ summary.tradingDaysChecked.length }} trading days
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <v-card flat rounded="lg" class="gap-backtest-card">
        <v-card-title class="d-flex justify-space-between align-center flex-wrap ga-3 px-5 py-4">
          <span class="text-h6 font-weight-medium">Per-stock detail</span>
          <span class="text-caption text-medium-emphasis">{{ stocks.length }} stock{{ stocks.length === 1 ? '' : 's' }}</span>
        </v-card-title>

        <v-data-table
          :headers="headers"
          :items="stocks"
          item-value="code"
          fixed-header
          :items-per-page="-1"
          hide-default-footer
          class="custom-data-table"
        >
          <template v-slot:[`item.historySufficient`]="{ item }">
            <v-chip size="small" variant="tonal" :color="item.historySufficient ? 'success' : 'error'">
              {{ item.historyDayCount }} day{{ item.historyDayCount === 1 ? '' : 's' }}
            </v-chip>
          </template>

          <template v-slot:[`item.gapEventCount`]="{ item }">
            <span :class="item.gapEventCount === 0 ? 'text-error' : ''">{{ item.gapEventCount }}</span>
          </template>

          <template v-slot:[`item.candleCoveragePct`]="{ item }">
            <span :class="item.candleCoveragePct < 100 ? 'text-error' : 'text-success'">
              {{ item.candleCoverageDays }}/{{ item.candleCoverageOfDays }} ({{ item.candleCoveragePct }}%)
            </span>
          </template>

          <template v-slot:[`item.corporateActionFlags`]="{ item }">
            <span v-if="!item.corporateActionFlags.length" class="text-medium-emphasis">—</span>
            <v-tooltip v-else location="top">
              <template v-slot:activator="{ props }">
                <v-chip v-bind="props" size="small" variant="tonal" color="error">
                  {{ item.corporateActionFlags.length }} flagged
                </v-chip>
              </template>
              <div v-for="f in item.corporateActionFlags" :key="f.date">
                {{ f.date }}: gap {{ f.gapPct }}% ({{ f.prevClose }} → {{ f.todayOpen }}){{ f.possibleRatio ? ` — possible ${f.possibleRatio}` : '' }}
              </div>
            </v-tooltip>
          </template>

          <template v-slot:no-data>
            <div class="py-6 text-center text-medium-emphasis">No watchlist stocks to assess.</div>
          </template>
        </v-data-table>
      </v-card>
    </div>
  </div>
</template>

<script>
import { fetchWrapper } from '@/helpers/fetch-wrapper';

export default {
  name: 'DataQuality-page',
  data() {
    return {
      loaded: true,
      summary: {},
      stocks: [],
      headers: [
        { title: 'Code', key: 'code' },
        { title: 'Daily history', key: 'historySufficient' },
        { title: 'Gap events', key: 'gapEventCount' },
        { title: 'Candle coverage', key: 'candleCoveragePct' },
        { title: 'Corp. action', key: 'corporateActionFlags' },
      ],
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    isFiniteNumber(value) {
      return value !== null && value !== undefined && Number.isFinite(Number(value));
    },
    load() {
      this.loaded = false;
      return fetchWrapper.get('api/data-quality').then((res) => {
        this.summary = res?.data?.summary || {};
        this.stocks = res?.data?.stocks || [];
        this.loaded = true;
      }).catch((err) => {
        console.error('Failed to load data quality report:', err);
        this.summary = {};
        this.stocks = [];
        this.loaded = true;
      });
    },
  },
};
</script>
